import axios from 'axios';
import type { Email, ActionLog, MailboxStat, ActionStat, CorrectionStats, Retour, CorpsComplet } from '../types';
import { authService } from './auth';

const API_BASE = process.env.REACT_APP_API_URL || 'https://email-classification.expnt.dev';

const api = axios.create({ baseURL: API_BASE });

// Attach credentials from sessionStorage (set by the login form, S110). The previous
// implementation read REACT_APP_ADMIN_* at build time, which baked the
// password into the public JS bundle — extractable via DevTools.
api.interceptors.request.use((config) => {
  const creds = authService.getCredentials();
  if (creds) {
    config.auth = creds;
  }
  return config;
});

// On 401, the stored credentials are stale (or were wrong). Clear them and
// reload so App.tsx re-evaluates auth state and shows the login form.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      authService.logout();
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

export const adminApi = {
  async getHealth() {
    const response = await api.get('/admin/health');
    return response.data;
  },

  async getDetailedKPIs(params?: any) {
    const response = await api.get('/admin/kpi/detailed', { params });
    return response.data;
  },

  async getRecentEmails(limit: number = 50): Promise<{ total: number; emails: Email[] }> {
    const response = await api.get('/admin/emails/recent', { params: { limit } });
    return response.data;
  },

  async enregistrerRetour(messageId: string, retour: Retour): Promise<Retour> {
    const response = await api.put(`/admin/emails/${encodeURIComponent(messageId)}/retour`, retour);
    return response.data;
  },

  async retirerRetour(messageId: string): Promise<void> {
    await api.delete(`/admin/emails/${encodeURIComponent(messageId)}/retour`);
  },

  async getCorpsComplet(messageId: string): Promise<CorpsComplet> {
    const response = await api.get(`/admin/emails/${encodeURIComponent(messageId)}/corps`);
    return response.data;
  },

  async getEmailDetails(messageId: string) {
    const response = await api.get(`/admin/emails/${messageId}`);
    return response.data;
  },

  async getRecentLogs(limit: number = 100): Promise<{ total: number; logs: ActionLog[] }> {
    const response = await api.get('/admin/logs/recent', { params: { limit } });
    return response.data;
  },

  async getMailboxStats(): Promise<{ mailboxes: MailboxStat[] }> {
    const response = await api.get('/admin/stats/mailboxes');
    return response.data;
  },

  async getActionStats(): Promise<{ actions: ActionStat[] }> {
    const response = await api.get('/admin/stats/actions');
    return response.data;
  },

  async getDeltaSyncStatus() {
    const response = await api.get('/admin/delta/status');
    return response.data;
  },

  async getCorrectionStats(): Promise<CorrectionStats> {
    const response = await api.get('/admin/stats/corrections');
    return response.data;
  },

  async resubscribe(webhookUrl?: string): Promise<{ status: string; previous_url: string; current_url: string; message: string }> {
    const params = webhookUrl ? { webhook_url: webhookUrl } : {};
    const response = await api.post('/admin/resubscribe', null, { params });
    return response.data;
  }
};
